import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { whatsappNumber } from '../data/mock';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MessageCircle, Clock, Calendar, Sparkles, User } from 'lucide-react';

const Booking = () => {
  const { t, language } = useLanguage();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const services = [
    { key: 'hygienic', label: language === 'en' ? 'Hygienic Manicure - $30 (45 min)' : 'Manucure Hygiénique - 30$ (45 min)' },
    { key: 'shellac', label: language === 'en' ? 'Shellac Manicure - $60 (90 min)' : 'Manucure Shellac - 60$ (90 min)' },
    { key: 'overlay', label: language === 'en' ? 'Gel Overlay - $70 (2 hours)' : 'Gel Overlay - 70$ (2 heures)' },
    { key: 'extensions', label: language === 'en' ? 'Gel Extensions - $95 (3-4 hours)' : 'Extensions Gel - 95$ (3-4 heures)' },
  ];

  const timeSlots = [
    '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM'
  ];

  const handleWhatsAppClick = () => {
    const serviceName = services.find(s => s.key === selectedService)?.label || (language === 'en' ? 'nail care' : 'soin des ongles');
    const fullName = `${firstName} ${lastName}`.trim() || (language === 'en' ? 'Not provided' : 'Non fourni');
    
    let message;
    if (language === 'en') {
      message = `Hello Anastasiya! I would like to book an appointment.\n\n✨ Name: ${fullName}\n✨ Service: ${serviceName}\n✨ Preferred Date: ${selectedDate || 'To be confirmed'}\n✨ Preferred Time: ${selectedTime || 'To be confirmed'}\n\nPlease let me know if this time is available. Thank you!`;
    } else {
      message = `Bonjour Anastasiya! Je souhaite prendre rendez-vous.\n\n✨ Nom: ${fullName}\n✨ Service: ${serviceName}\n✨ Date souhaitée: ${selectedDate || 'À confirmer'}\n✨ Heure souhaitée: ${selectedTime || 'À confirmer'}\n\nMerci de me confirmer la disponibilité!`;
    }
    
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <section id="booking" className="py-24 bg-gradient-to-b from-rose-50/50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-rose-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-10 w-64 h-64 bg-stone-100/50 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Booking Form Section */}
          <div>
            <span className="text-rose-400 text-sm tracking-widest uppercase font-medium">
              WhatsApp
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-800 mt-3 mb-4">
              {t.booking.title}
            </h2>
            <div className="w-16 h-1 bg-rose-300 mb-6" />
            <p className="text-stone-600 text-lg mb-8">
              {t.booking.subtitle}
            </p>

            {/* Booking Form */}
            <div className="space-y-5 mb-8">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-stone-700 font-medium flex items-center gap-2">
                    <User className="w-4 h-4 text-rose-400" />
                    {language === 'en' ? 'First Name' : 'Prénom'}
                  </Label>
                  <Input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={language === 'en' ? 'Your first name' : 'Votre prénom'}
                    className="h-12 border-stone-200 focus:border-rose-400 focus:ring-rose-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-stone-700 font-medium">
                    {language === 'en' ? 'Last Name' : 'Nom'}
                  </Label>
                  <Input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={language === 'en' ? 'Your last name' : 'Votre nom'}
                    className="h-12 border-stone-200 focus:border-rose-400 focus:ring-rose-400"
                  />
                </div>
              </div>

              {/* Service Selection */}
              <div className="space-y-2">
                <Label htmlFor="service" className="text-stone-700 font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-rose-400" />
                  {language === 'en' ? 'Select Service' : 'Choisir le service'}
                </Label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="w-full h-12 border-stone-200 focus:border-rose-400 focus:ring-rose-400">
                    <SelectValue placeholder={language === 'en' ? 'Choose a service...' : 'Choisir un service...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.key} value={service.key}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Selection */}
              <div className="space-y-2">
                <Label htmlFor="date" className="text-stone-700 font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-rose-400" />
                  {language === 'en' ? 'Preferred Date' : 'Date souhaitée'}
                </Label>
                <Input
                  type="date"
                  id="date"
                  min={today}
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-12 border-stone-200 focus:border-rose-400 focus:ring-rose-400"
                />
              </div>

              {/* Time Selection */}
              <div className="space-y-2">
                <Label htmlFor="time" className="text-stone-700 font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-rose-400" />
                  {language === 'en' ? 'Preferred Time' : 'Heure souhaitée'}
                </Label>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger className="w-full h-12 border-stone-200 focus:border-rose-400 focus:ring-rose-400">
                    <SelectValue placeholder={language === 'en' ? 'Choose a time...' : 'Choisir une heure...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Main CTA */}
            <Button
              onClick={handleWhatsAppClick}
              size="lg"
              className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <MessageCircle className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
              {t.booking.cta}
            </Button>

            <p className="text-stone-400 text-sm text-center mt-4">
              {language === 'en' 
                ? 'Your booking request will be sent via WhatsApp for confirmation'
                : 'Votre demande sera envoyée via WhatsApp pour confirmation'}
            </p>
          </div>

          {/* Hours Section */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl font-medium text-stone-800">
                  {t.booking.hours}
                </h3>
              </div>

              <div className="space-y-3">
                {t.booking.schedule.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between py-3 px-4 rounded-lg ${
                      item.hours === 'Fermé' || item.hours === 'Closed'
                        ? 'bg-stone-50 text-stone-400'
                        : 'bg-rose-50/50 text-stone-700'
                    }`}
                  >
                    <span className="font-medium">{item.day}</span>
                    <span className={item.hours === 'Fermé' || item.hours === 'Closed' ? 'italic' : ''}>
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>

              {/* Location Badge */}
              <div className="mt-8 p-4 bg-stone-800 rounded-xl text-center">
                <p className="text-stone-300 text-sm uppercase tracking-widest mb-1">
                  Supreme Beauty & Wellness
                </p>
                <p className="text-white font-medium">
                  44 Mapleton Rd., suite 202
                </p>
                <p className="text-stone-400 text-sm">
                  Moncton, NB
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Booking;
